import * as Rx from 'rxjs';
import { last, map, switchMap } from 'rxjs/operators';

import type {
  IProduct,
  IBasic,
  ProductOffer,
  CreateOfferPayload,
  UpdateOfferPayload,
  GetProductOffersParams,
  SubscriptionPlan,
  PlanChangeConfig,
  SetPlanChangeConfigPayload,
  IProductCreateInfo,
} from 'v2/interfaces/product';

import apiService from './api';

import type { ApiService } from './api';
import type { IrregularProduct } from 'scenes/Producer/Product/MyProducts/constants';

export class ProductService {
  private product$ = new Rx.ReplaySubject<IProduct>(1);

  constructor(private apiService: ApiService) {}

  public get(): Rx.Observable<IProduct> {
    return this.product$;
  }

  public load(id: number): void {
    this.apiService.get(`newproduct/${id}`).subscribe((product: IProduct) => this.product$.next(product));
  }

  //TODO rever
  public set(product: Partial<IProduct>): void {
    this.product$.pipe(last()).subscribe((currentState: IProduct) => {
      this.product$.next({ ...currentState, ...product });
    });
  }

  public saveGeneralBasic(): void {
    this.product$
      .pipe(
        map((product: IProduct): IBasic => {
          const basic: IBasic = {
            id: product.id,
            title: product.title,
            categories: product.categories,
            description: product.description,
            type: product.type,
            image: product.image,
          };
          return basic;
        }),
        switchMap((model) => this.apiService.put(`newproduct/${model.id}/general/basic`, model))
      )
      .subscribe();
  }

  public savePins(productId: string, pins: string[]) {
    return this.apiService.post(`/products/${productId}/deliverymethods/createbatch`, { pins });
  }

  public activateConversionTools() {
    return this.apiService.post('/products/activate-conversion-tools', {});
  }

  public getCoproducers(productId: number) {
    return this.apiService.get(`/products/${productId}/coproducers`).pipe(map((res) => res.data));
  }

  public updateCoproducers(productId: number, coproducers: any) {
    return this.apiService.put(`/products/${productId}/coproducers`, {
      coproducers,
    });
  }

  public getProductVariations(parentId: number): Promise<IProduct[]> {
    return this.apiService
      .get(`/products?parent_id=${parentId}&per_page=1000`)
      .pipe(map((res) => res.data))
      .toPromise();
  }

  public getIrregularProductVariations(parentId: number): Promise<IrregularProduct[]> {
    return this.apiService
      .get(`/products/irregular?parent_id=${parentId}&per_page=1000`)
      .pipe(map((res) => res.data))
      .toPromise();
  }

  public getOffers(productId: number, params: GetProductOffersParams = {}): Promise<ProductOffer[]> {
    return this.apiService
      .get(`/products/${productId}/offers`, {
        ...params,
        per_page: 100,
      })
      .pipe(map((res) => res.data))
      .toPromise();
  }

  public getPlans(parentId: number): Promise<SubscriptionPlan[]> {
    return this.apiService
      .get(`/products/${parentId}/plans?per_page=100`)
      .pipe(map((response) => response.data))
      .toPromise();
  }

  public getPlanChangeConfig(planId: number): Promise<PlanChangeConfig> {
    return this.apiService
      .get(`/products/${planId}/planchangeconfig`)
      .pipe(map((response) => response.data))
      .toPromise();
  }

  public setPlanChangeConfig({ targetPlanId, ...payload }: SetPlanChangeConfigPayload): Promise<void> {
    return this.apiService
      .post(`/products/${targetPlanId}/planchangeconfig`, {
        ...payload,
      })
      .toPromise();
  }

  public clonePlan(planId: number): Promise<SubscriptionPlan> {
    return this.apiService
      .post(`/products/${planId}/clone`, {})
      .pipe(map((response) => response.data))
      .toPromise();
  }

  public deletePlan(id: number): Promise<SubscriptionPlan> {
    return this.apiService
      .put(`/products/${id}/changestatus?status=2`, {})
      .pipe(map((response) => response.data))
      .toPromise();
  }

  public createOffer(productId: number, offer: CreateOfferPayload): Promise<void> {
    return this.apiService.post(`/products/${productId}/offers`, offer).toPromise();
  }

  public updateOffer(productId: number, offerId: number, offer: UpdateOfferPayload): Promise<ProductOffer> {
    return this.apiService
      .put(`/products/${productId}/offers/${offerId}`, offer)
      .toPromise()
      .then((res) => res.data);
  }

  public desactivateOffer(productId: number, offerId: number): Promise<void> {
    return this.apiService
      .put(`/products/${productId}/offers/${offerId}`, {
        is_active: 0,
      })
      .toPromise();
  }

  public getCreateInfo(): Promise<IProductCreateInfo> {
    return this.apiService
      .get('/products/create-info')
      .pipe(map((response) => response.data))
      .toPromise();
  }
}

const productService = new ProductService(apiService);
export default productService;
